import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { useNavigate } from "react-router-dom";
import StatusOrderChip from "@/app/global-components/StatusOrderChip";
import { isUndefined } from "lodash";
import TableComponent from "@/app/global-components/table";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";
import { routes } from "@/consts";
import useOrder from "@/hooks/use-order";
import {
  BiPackage,
  BiBuilding,
  BiDollarCircle,
  BiCalendar,
  BiCheckCircle,
} from "react-icons/bi";

interface Product {
  itemId: string;
  item: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  title: string;
  deliverDate: string;
  address: string;
  products: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  status: string;
  tender: { title: string };
  company: {
    nameAr: string;
    CompanyNr: string;
  };
}

const OrdersBList: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate();
  const { fetchBuyerOrders } = useOrder();
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const [listOrders, setListOrders] = useState<Order[]>([]);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (myCompany?.id) {
          const result = await fetchBuyerOrders(myCompany.id);
          setListOrders(result?.data || []);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      }
    };
    fetchOrders();
  }, [myCompany?.id]);

  const calculateTotalPrice = (products: Product[]) =>
    products.reduce(
      (sum, product) => sum + product.price * product.quantity,
      0
    );

  const columns = [
    {
      title: (
        <div className="flex items-center gap-2">
          <BiPackage className="w-4 h-4 text-blue-600" />
          {t("orders.title")}
        </div>
      ),
      field: "title",
      render: (row: Order) => (
        <div className="font-medium text-gray-900">
          {row.title || row.tender?.title || `Order #${row.OrderNr}`}
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <BiBuilding className="w-4 h-4 text-green-600" />
          {t("company.fields.name")}
        </div>
      ),
      field: "companyName",
      render: (row: Order) => (
        <div className="text-gray-700">
          {row.company?.nameAr || row.company?.CompanyNr || "-"}
        </div>
      ),
      hideOnMobile: true,
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <BiDollarCircle className="w-4 h-4 text-purple-600" />
          {t("orders.fields.totalPrice")}
        </div>
      ),
      field: "totalPrice",
      render: (row: Order) => (
        <div className="font-semibold text-purple-700">
          {calculateTotalPrice(row.products).toFixed(2)} {t("currency")}
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <BiCalendar className="w-4 h-4 text-orange-600" />
          {t("orders.fields.deliveryDate")}
        </div>
      ),
      field: "deliverDate",
      render: (row: Order) => (
        <div className="text-gray-700">
          {new Date(row.deliverDate).toLocaleDateString()}
        </div>
      ),
      hideOnMobile: true,
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <BiCheckCircle className="w-4 h-4 text-indigo-600" />
          {t("orders.fields.status")}
        </div>
      ),
      field: "status",
      render: (row: Order) => <StatusOrderChip status={row.status} />,
      sortable: true,
    },
  ];

  const handleRowClick = (row: Order) => {
    navigate(`${routes.order}/${row.id}`);
  };

  return (
    <div className="p-4">
      <TableComponent
        columns={columns}
        data={listOrders}
        onRowClick={handleRowClick}
        rowKeyField="id"
        title={`${t("orders.listTitle")} ${
          userRole === ROLE.SELLER ? t("general.sales") : t("general.Purchases")
        }`}
        subtitle={`${listOrders.length} ${t("statistics.ordersFound")}`}
        searchable={true}
        sortable={true}
        variant="elevated"
        size="md"
        maxHeight={400}
        emptyStateMessage="No orders found"
        emptyStateSubmessage="Your orders will appear here once you place them"
      />
    </div>
  );
};

export default OrdersBList;
